import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Uyo} from '~/CreateFabricGreetingCard/components/Uyo/Uyo';
import {getEventId} from '~/CreateFabricGreetingCard/reducers/eviteEvent/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricGreetingCard/reducers/fabricDesign/constants';
import {
  selectFabricArt,
  selectPristineUyo,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {SET_TEMPLATE_SNAPSHOT} from '~/CreateFabricGreetingCard/reducers/fabricTemplate/constants';
import {Canvas} from '~/common/canvas/Canvas';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {Envelope} from '~/common/fabric/Card/components/Envelope/Envelope';
import {Container} from '~/common/fabric/CreateDesign/components/Container/Container';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {FlipIcon} from '~library/svg/FlipIcon';
import {largishAndUp, tanzanite} from '~libSass/base/_exports.sass';
import {UyoControl} from './components/UyoControl';
import '~/common/fabric/Card/components/FlipCard/FlipCard.sass';
import './GreetingCardTemplate.sass';

export const GreetingCardTemplate = () => {
  const [flipped, setFlipped] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({width: 0, height: 0});
  const {setCanvas, setActiveObjects} = useContext(FabricContext);
  const fabricArt = useSelector(selectFabricArt);
  const pristineUyo = useSelector(selectPristineUyo);
  const eventId = useSelector(getEventId);
  const dispatch = useDispatch();
  const elementRef = useRef(null);
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const isMobile = !isDesktop;

  const saveSnapshot = useCallback(
    (snapshot) => {
      dispatch({type: SET_TEMPLATE_SNAPSHOT, payload: snapshot});
    },
    [dispatch]
  );

  const onCanvasLoadHandler = useCallback(() => {
    dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {loaded: true}});
  }, [dispatch]);

  const onChangeHandler = (newFabricArt) => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {
        fabricArt: newFabricArt,
      },
    });
  };

  const onCardFlip = useCallback(() => setFlipped((prevState) => !prevState), []);

  useEffect(() => {
    const handleCanvasResize = (event) => {
      setCanvasDimensions(event.detail);
    };

    window.addEventListener('canvas:resized', handleCanvasResize);

    return () => {
      window.removeEventListener('canvas:resized', handleCanvasResize);
    };
  }, []);

  return (
    <Container className="greeting-card-template">
      <div className="card_template card_template--no-editor" ref={elementRef} id="card-inner">
        <div className="card_template__images">
          <Envelope
            type="flap_down_image"
            className="card_template__envelope"
            redirectTo={isMobile ? null : Sections.ENVELOPE_LINER}
            parentNode={`${isMobile ? 'fabric-canvas' : ''}`}
          />
          <div className={`flip-card ${flipped ? 'flipped' : ''}`.trimEnd()}>
            <Button
              variant="raised"
              size="small"
              className="flip-card__button"
              onClick={onCardFlip}
              data-qa-id="flipCardButton"
              type="button"
            >
              <FlipIcon ratio={0.7} color={tanzanite} />
              <Typography as="span" variant="link" size="medium" data-qa-id="flipCardText">
                Flip card
              </Typography>
            </Button>
            <div className="flip-card-inner">
              {!flipped && (
                <Uyo
                  className={!flipped ? 'animated-show' : ''}
                  pristineUyo={pristineUyo}
                  eventId={eventId}
                />
              )}
              <Canvas
                configJson={fabricArt}
                onChange={onChangeHandler}
                setCanvas={setCanvas}
                setActiveObjects={setActiveObjects}
                saveSnapshot={saveSnapshot}
                onLoad={onCanvasLoadHandler}
                className="card_template__canvas_mobile flip-card-front"
                parentNode={`${isMobile ? 'create-container' : 'card-inner'}`}
              />
              <div
                className="fabric_user_card card_template__canvas_mobile flip-card-back"
                style={
                  canvasDimensions.width
                    ? {
                        width: `${canvasDimensions.width}px`,
                        height: `${canvasDimensions.height}px`,
                      }
                    : undefined
                }
              >
                <div className="flip-card-back__content empty">
                  <span className="flip-card-back__content__text empty" data-qa-id="card-back-text">
                    Optional: Write your
                    <br />
                    customized message
                    <br />
                    here in a later step.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isDesktop && !flipped && (
        <UyoControl className={!flipped ? 'animated-show' : ''} eventId={eventId} />
      )}
    </Container>
  );
};
